import { useState, useEffect, useCallback } from 'react'

interface TourStatus {
  hasCompletedTour: boolean
  completedAt?: string
  lastSkippedAt?: string
}

interface UseProductTourResult {
  shouldShowTour: boolean
  isLoading: boolean
  startTour: () => void
  completeTour: () => Promise<void>
  skipTour: () => Promise<void>
  resetTour: () => Promise<void>
  tourStatus: TourStatus | null
}

/**
 * Custom hook to manage product tour state and persistence
 * @param tourType - The type of tour (admin, tenant, platform)
 * @param autoShow - Whether to automatically show tour on first visit (default: true)
 * @returns Tour management functions and state
 */
export const useProductTour = (
  tourType: string,
  autoShow: boolean = true
): UseProductTourResult => {
  const [shouldShowTour, setShouldShowTour] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [tourStatus, setTourStatus] = useState<TourStatus | null>(null)

  // Load tour status from backend
  const loadTourStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/tour-status', {
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()

        // SECURITY FIX: Validate JSON response before accessing properties
        if (data && typeof data === 'object' && 'hasCompletedTour' in data) {
          setTourStatus(data)

          // Auto-show tour if user hasn't completed it and autoShow is enabled
          if (!data.hasCompletedTour && autoShow) {
            setShouldShowTour(true)
          }
        } else {
          console.warn('Invalid tour status response format:', data)
          // Set default tour status if response is invalid
          setTourStatus({ hasCompletedTour: false })
        }
      } else if (response.status === 401) {
        // Silently handle authentication errors - tour is optional
        setTourStatus({ hasCompletedTour: false })
      }
    } catch (error) {
      // Silently handle network errors - tour is optional
      setTourStatus({ hasCompletedTour: false })
    } finally {
      setIsLoading(false)
    }
  }, [autoShow])

  useEffect(() => {
    loadTourStatus()
  }, [loadTourStatus])

  // Manually start the tour
  const startTour = useCallback(() => {
    setShouldShowTour(true)
  }, [])

  // Mark tour as completed
  const completeTour = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/tour-status', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ completed: true })
      })

      if (response.ok) {
        const data = await response.json()
        setTourStatus(data.status)
        setShouldShowTour(false)
      }
    } catch (error) {
      console.error('Failed to complete tour:', error)
    }
  }, [])

  // Mark tour as skipped
  const skipTour = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/tour-status', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ completed: false, skipped: true })
      })

      if (response.ok) {
        const data = await response.json()
        setTourStatus(data.status)
        setShouldShowTour(false)
      }
    } catch (error) {
      console.error('Failed to skip tour:', error)
    }
  }, [])

  // Reset tour status (allows retaking)
  const resetTour = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/tour-status', {
        method: 'DELETE',
        credentials: 'include',
        headers
      })

      if (response.ok) {
        setTourStatus({
          hasCompletedTour: false
        })
        setShouldShowTour(true)
      }
    } catch (error) {
      console.error('Failed to reset tour:', error)
    }
  }, [])

  return {
    shouldShowTour,
    isLoading,
    startTour,
    completeTour,
    skipTour,
    resetTour,
    tourStatus
  }
}
